import { useCallback, useEffect, useMemo, useState } from 'react'
import { maxText, safeParseDate, setDocumentTitle } from 'shared/util'

import {
	Box, Paper, Stack, StackProps,
	useMediaQuery,
	useTheme
} from '@mui/material'

import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'
import { BreadCrumbItem } from 'components/topBar/types'

import { SearchDetailResult } from 'interface/search'

import { useTenant } from 'features/tenant/context'
import { TargetListResult } from 'interface/search/targetList'
import { MrpLogResult, MrpProjectResult } from 'interface/carenet/mrp'
import { LvLogResult, LvProjectResult } from 'interface/carenet/lv'

import { useGrid } from 'components/grid'
import { GridBar } from './components/gridBar'
import { ProjectBar } from './components/projectBar'

import { TargetPage } from './pages/target'

import { SourceProject } from './types'
import { KojinResult, useListKojin } from 'interface/kojin'
import { useEvaluationForm } from 'features/search/context'
import { KojinResultWithEvaluationSummary, useListEvaluationResults } from 'interface/search/evaluation'
import { evaluationSummary } from 'interface/search/evaluation/util'

type TargetGridPageProps = {
  search: SearchDetailResult
	topBarState: ReturnType<typeof useTopBar>
	sideMenuState: ReturnType<typeof useSideMenu>
  targetList: TargetListResult | null
  mrpProject: MrpProjectResult | null
	mrpLogList: MrpLogResult[]
  lvProject: LvProjectResult | null
	lvLogList: LvLogResult[]
} & StackProps
export const TargetGridPage = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
  search,
  targetList,
  mrpProject,
	mrpLogList,
  lvProject,
	lvLogList,
  ...others
}: TargetGridPageProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))

	const tenant = useTenant()
	const evaluationForm = useEvaluationForm()
	const gridState = useGrid()

	const [projectList, setProjectList] = useState<SourceProject[]>(
		() => {
			const projectList: SourceProject[] = []
			if (mrpProject) {
				projectList.push({
					source: 'mrp',
					isOrigin: true,
					projectId: mrpProject.projectId,
					projectName: mrpProject.projectName ?? '',
					productName: mrpProject.productName ?? null,
					projectDate: safeParseDate(mrpProject.startDate) ?? null,
					logList: mrpLogList
				})
			}
			if (lvProject) {
				projectList.push({
					source: 'lv',
					isOrigin: true,
					projectCode: lvProject.projectCode,
					projectName: lvProject.projectName ?? '',
					productName: lvProject.productName ?? null,
					projectDate: safeParseDate(lvProject.broadcastStartDate) ?? null,
					logList: lvLogList
				})
			}
			return projectList
		}
	)
	const onFetched = useCallback(
		(result: KojinResult[]) => {
			result.sort((a, b) => {
				if (a.target?.sequence && b.target?.sequence) {
					return a.target?.sequence - b.target?.sequence
				}
				if (a.score?.rank && b.score?.rank) {
					return a.score.rank - b.score.rank
				}
				if (a.isiNameKana && b.isiNameKana) {
					return a.isiNameKana.localeCompare(b.isiNameKana)
				}
				return 0
			})
		},
		[]
	)
	const listState = useListKojin(
		search.searchId,
		{
			targetListId: targetList?.listId,
			mrpProjectId: mrpProject?.projectId,
			lvProjectCode: lvProject?.projectCode
		}, {
			updateTime: maxText(search.updateTime, targetList?.updateTime) ?? undefined,
			onFetched
		}
	)
	const evaluationResultState = useListEvaluationResults(
		search.searchId,
		search.evaluationUploadTime
	)

	const progress = useMemo(
		() => (listState.loaded && listState.total) ? (
			(listState.loaded / listState.total) * 100
		) : undefined,
		[listState]
	)

	const dataList = useMemo<KojinResultWithEvaluationSummary[]>(
		() => {
			if (listState.loading || evaluationResultState.loading) return []
			const kojinList = listState.data
			if (!kojinList || kojinList.length === 0) return []
			if (!evaluationForm) return kojinList
			const evaluationList = evaluationResultState.data?.list
			if (!evaluationList || evaluationList.length === 0) return kojinList
			return kojinList.map(
				kojin => {
					if (!kojin.kojinCode) return kojin
					const kojinsEvaluationList = evaluationList.filter(r => r.kojinCode === kojin.kojinCode)
					if (kojinsEvaluationList.length === 0) return kojin
					return { ...kojin, evaluation: evaluationSummary(evaluationForm, kojinsEvaluationList) }
				}
			)
		},
		[listState, evaluationResultState, evaluationForm]
	)

	const onChangeEvaluation = useCallback(
		(/*kojinCode: string*/) => {
			evaluationResultState.refetch()
		},
		[evaluationResultState]
	)

	useEffect(
		() => {
      const crumbs: BreadCrumbItem[] = []
      if (search.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
      crumbs.push({
        label: search.title,
        url: `/${tenant.domain}/search/${search.searchId}/home` + (
					targetList ? '?tabs=targetList' :
					(mrpProject || lvProject) ? '?tabs=project' : ''
				)
      })
			if (targetList?.listName) {
				crumbs.push({ label: targetList.listName })
				setDocumentTitle(targetList.listName, search.title, tenant.tenantName)
			} else if (mrpProject?.projectName) {
				crumbs.push({ label: mrpProject.projectName })
				setDocumentTitle(mrpProject.projectName, search.title, tenant.tenantName)
			} else if (lvProject?.projectName) {
				crumbs.push({ label: lvProject.projectName })
				setDocumentTitle(lvProject.projectName, search.title, tenant.tenantName)
			}
      setBreadCrumbs(crumbs)
			setActivePage('grid')
		},
		[tenant, setBreadCrumbs, setActivePage, search, targetList, mrpProject, lvProject]
	)
	return (
		<Stack
			direction="column"
			sx={{
				position: 'relative',
				overflow: 'hidden',
			}}
			{...others}
		>
			<Paper square sx={{ width: '100%' }}>
				<Stack direction="row" spacing={1} alignItems={'center'}>
					<ProjectBar search={search} projectList={projectList} setProjectList={setProjectList}/>
					<Box flexGrow={1}/>
					<GridBar {...gridState}/>
				</Stack>
			</Paper>

			<Box
				flexGrow={1}
				boxSizing="border-box"
				width="100%"
				overflow="hidden"
				position="relative"
				padding={isSmallMedia ? 0 : 1}
			>
				<TargetPage
					search={search}
					saveKey={targetList ? "target_grid" : "project_grid"}
					sheetName={(
						targetList ? targetList.listName :
						mrpProject ? mrpProject.projectName :
						lvProject ? lvProject.projectName : null
					) ?? undefined}
					loading={listState.loading || evaluationResultState.loading}
					progress={progress}
					kojinList={dataList}
					evaluationForm={evaluationForm}
					categories={targetList?.category}
					scores={targetList?.score}
					projectList={projectList}
					onChangeEvaluation={onChangeEvaluation}
					{...gridState}
				/>
			</Box>
		</Stack>
	)
}
